// Game Hall interior layout (both floors), 1280×720 world coords.
// INITIAL ESTIMATES traced from the background art — refine in dev with
// SceneEditMode (press E) + CoordsDebug, then paste the refined values here.

// Floor polylines: [x, y] sprite-centre points, same semantics as
// SECOND_FLOOR_POLYLINE (WalkController.getFloorYAt interpolates between them).
// DOWN_FLOOR refined in dev (2026-07-08) with SceneEditMode/grid overlay against the
// bg_game_hall_down art — the original estimate sat well behind the real back wall,
// which made the door/stairs trigger zones geometrically unreachable (feet could never
// get far enough back to enter them). Re-traced along the visible baseboard.
// Hand-tuned in the DEV edit mode 2026-07-09.
export const DOWN_FLOOR = [[3, 477], [271, 440], [376, 460], [581, 426], [587, 388], [1277, 449]];
// UP_FLOOR refined in dev (2026-07-08, Task 4) with SceneEditMode/grid overlay against the
// bg_game_hall_up art. The initial estimate (flat ~470-620 band) sat well IN FRONT of the
// real back wall/balustrade, wasting most of the room and leaving UP_STAIRS_ZONE completely
// unreachable (see UP_OBSTACLES note below). Re-traced along the shelf-cabinet front edge
// (left corner), the balustrade base (the railing overlooking the stairwell void, which dips
// closer to the back wall as it recedes toward the right), and the bookshelf/armchair front
// edge on the right wall.
// Hand-tuned in the DEV edit mode 2026-07-09.
export const UP_FLOOR   = [[7, 453], [541, 336], [627, 295], [787, 269], [790, 300], [1082, 323], [1276, 346]];

// Obstacle rectangles the FEET may not enter (tables incl. chairs, counter, sofa,
// stair base, plants). {x, y, w, h} with x/y = top-left.
// DOWN_OBSTACLES refined alongside DOWN_FLOOR (see note above) — shifted up to match
// the counter/stairs/sofa positions in the art, and re-split so a walkable gap exists
// between the stairs base and the centre table (DOWN_STAIRS_ZONE lives in that gap).
export const DOWN_OBSTACLES = [
  // Hand-tuned in the DEV edit mode 2026-07-09 (walk-behind corridors preserved: behind
  // t1 between the counter base and the tabletop, and the pass-by lane behind t2).
  { x: 260, y: 300, w: 320, h: 150, what: 'lada (counter)' },
  { x: 600, y: 250, w: 190, h: 150, what: 'schody (stairs base)' },
  { x: 790, y: 333, w: 220, h: 74, what: 'kanapa (sofa) + ława (coffee table)' },
  { x: 265, y: 520, w: 350, h: 200, what: 'stolik t1 (square, front-left)' },
  { x: 678, y: 451, w: 215, h: 105, what: 'stolik t2 (round, centre)' },
  { x: 953, y: 385, w: 180, h: 117, what: 'stolik t3 (round, right)' },
  { x: 0, y: 400, w: 78, h: 320, what: 'rośliny przy wejściu (plants, left)' },
  { x: 1203, y: 401, w: 78, h: 320, what: 'rośliny (plants, right corner)' },
];
// UP_OBSTACLES refined alongside UP_FLOOR above (Task 4). The original 'balustrada schodów'
// rectangle was a crude axis-aligned stand-in for the railing that actually runs diagonally
// (it recedes toward the back wall as x increases) — that shape now lives in UP_FLOOR's back
// boundary instead of a separate obstacle rect, and it (plus the too-far-forward floor line)
// was what made UP_STAIRS_ZONE geometrically unreachable: the zone sat entirely inside the
// railing rect ∪ the round table's rect, both of which were shifted down/right of their real
// art positions. Table boxes below are re-traced to the round/rect table + chair footprints;
// a walkable gap is kept between the round table (t4) and the mid table/armchair cluster (t6)
// for UP_STAIRS_ZONE to live in.
// Re-traced 2026-07-08 (post-release polish): the previous boxes were shifted/oversized vs
// the art (t6 reached ~190px past its table, t5 missed its left chairs), blocking open
// floor. Boxes now hug the drawn furniture groups; lanes: behind t4 (feet <390), between
// stairs zone and t6 (x500–575), between t6 (bottom 440) and t5 (top 465), right of t5
// (x755+), and around the reading nook via x885–930.
export const UP_OBSTACLES = [
  // Hand-tuned in the DEV edit mode 2026-07-09.
  { x: 93, y: 367, w: 240, h: 163, what: 'stolik t4 (round, left)' },
  { x: 442, y: 451, w: 270, h: 234, what: 'stolik t5 (square, front-centre)' },
  { x: 688, y: 318, w: 169, h: 89, what: 'stolik t6 + fotel L (rect table + armchair, centre)' },
  { x: 1047, y: 325, w: 168, h: 110, what: 'stolik t7 (rect, right)' },
  { x: 632, y: 177, w: 250, h: 100, what: 'regalik + sofka (back wall, centre)' },
  { x: 787, y: 229, w: 112, h: 70, what: 'regały (bookshelves, back wall right)' },
  { x: 941, y: 251, w: 70, h: 77, what: 'fotel (armchair, centre-right)' },
  { x: 3, y: 501, w: 21, h: 134, what: 'roślina (plant, left corner)' },
  { x: 1230, y: 480, w: 46, h: 134, what: 'roślina (plant, right corner)' },
];

// Trigger zones (feet rectangles).
// DOWN_EXIT_DOOR / DOWN_STAIRS_ZONE refined with DOWN_FLOOR/DOWN_OBSTACLES above so both
// are actually reachable: door zone sits on the entry mat in front of the glass doors;
// stairs zone sits in the walkable gap between the stairs-base obstacle and the centre
// table obstacle (the original estimate put it fully inside the table's rect).
export const DOWN_EXIT_DOOR   = { x: 90,  y: 400, w: 150, h: 110 };  // left wall door → street
// Standing in front of the counter (feet strip just below its obstacle rect) offers "Kup gry".
// Standing zone in front of the counter (FEET coords — must be walkable; the user-drawn
// counter rect itself became the clickable area in GameHallScene). X-range hand-tuned
// 2026-07-09; y = the walkable strip between the counter base (450) and table t1 (520).
export const DOWN_COUNTER_ZONE = { x: 272, y: 445, w: 310, h: 70 };
// Height capped at 30 (feet y400–430) so a pass-by lane (feet y430–460) runs behind the
// centre table t2 — you can cross the room without being pulled upstairs; walking NORTH
// into the staircase still triggers.
export const DOWN_STAIRS_ZONE = { x: 610, y: 400, w: 170, h: 30 };   // stair base → upstairs
// Ranking board: the "HOUSE RULES" framed easel on the counter, just right of the
// bookshelf (art coords traced 2026-07-09 with a grid overlay — frame ~x527-559,
// y283-338; padded a bit on all sides for a comfortable click target). Clicking it
// opens RankingModal when standing near the counter (same idiom as DOWN_COUNTER_ZONE).
export const DOWN_RANKING_ZONE = { x: 512, y: 264, w: 66, h: 88 };
// UP_STAIRS_ZONE refined in dev (2026-07-08, Task 4) — moved into the walkable gap between
// the round table (t4) and the mid table/armchair cluster (t6), right by the near end of the
// balustrade (there's no literal staircase drawn upstairs — the railing overlooks the
// stairwell void the ground-floor staircase leads up into, so the trigger sits at its edge).
// Re-anchored 2026-07-09 to hug the hand-tuned UP_FLOOR line (the old zone sat almost
// entirely above the new walkable band).
export const UP_STAIRS_ZONE   = { x: 430, y: 330, w: 130, h: 45 };   // stairwell → downstairs

// Tables: tabletop sprite anchor (centre of the green felt, display width w) and one
// seat per chair (FEET coords at the chair; nickname renders above). Seat ids match
// backend realtime/tables.py TABLES. Values verified in dev (2026-07-08, Task 5) with a
// per-seat/per-top marker overlay over bg_game_hall_{down,up}: all table tops sit on their
// felt and every seat anchor lands on/near its chair. Seat anchors are deliberately NOT
// raised toward the counter (t1:W stays at y610) to keep the SIT_RADIUS=55 offer clear
// of DOWN_COUNTER_ZONE.
// top: {x, y} = centre of the visible felt; {w, h} = the felt's drawn extent — the board
// sprite is CONTAIN-fit into w×h so it never overflows the tabletop; depth = the table's
// foreground-cutout ground line + 1 (sceneForeground.js), so the board draws ON the felt,
// above the cutout mask (which otherwise re-draws bg over sprites) yet below anyone
// standing in front of the table.
// board: default placement box for ANY game's board on this table (hand-tuned in the
// DEV edit mode 2026-07-09); per-game BOARD_OVERRIDES win over it, top is the fallback.
export const DOWN_TABLES = [
  { id: 't1', top: { x: 445, y: 554, w: 230, h: 100, depth: 720 },
    board: { x: 441, y: 550, w: 96, h: 72 },
    seats: [{ id: 'N', x: 350, y: 515 }, { id: 'W', x: 300, y: 610 }, { id: 'E', x: 569, y: 529 }, { id: 'S', x: 592, y: 627 }] },
  { id: 't2', top: { x: 777, y: 436, w: 154, h: 34, depth: 556 },
    board: { x: 773, y: 436, w: 42, h: 36 },
    seats: [{ id: 'N', x: 691, y: 423 }, { id: 'W', x: 707, y: 489 }, { id: 'E', x: 839, y: 425 }, { id: 'S', x: 881, y: 481 }] },
  { id: 't3', top: { x: 1032, y: 411, w: 134, h: 24, depth: 536 },
    board: { x: 1032, y: 411, w: 36, h: 24 },
    seats: [{ id: 'N', x: 954, y: 433 }, { id: 'W', x: 1032, y: 452 }, { id: 'E', x: 1093, y: 395 }, { id: 'S', x: 1110, y: 450 }] },
];
export const UP_TABLES = [
  { id: 't4', top: { x: 229, y: 403, w: 172, h: 61, depth: 556 },
    board: { x: 226, y: 403, w: 71, h: 42 },
    seats: [{ id: 'N', x: 178, y: 400 }, { id: 'W', x: 110, y: 469 }, { id: 'E', x: 300, y: 384 }, { id: 'S', x: 321, y: 461 }] },
  { id: 't5', top: { x: 600, y: 489, w: 215, h: 115, depth: 701 },
    board: { x: 596, y: 478, w: 107, h: 65 },
    seats: [{ id: 'NW', x: 478, y: 455 }, { id: 'NE', x: 682, y: 448 }, { id: 'SW', x: 471, y: 571 }, { id: 'SE', x: 721, y: 542 }] },
  { id: 't6', top: { x: 780, y: 322, w: 138, h: 39, depth: 416 },
    board: { x: 780, y: 322, w: 45, h: 34 },
    // Corrected 2026-07-09: the art shows exactly 4 chairs (left, front, back-right, right).
    seats: [{ id: 'N', x: 825, y: 328 }, { id: 'W', x: 645, y: 385 }, { id: 'E', x: 858, y: 392 }, { id: 'S', x: 722, y: 402 }] },
  { id: 't7', top: { x: 1130, y: 345, w: 173, h: 53, depth: 471 },
    board: { x: 1130, y: 343, w: 53, h: 32 },
    // Corrected 2026-07-09: 4 chairs — the back (N) one peeks out from behind the table
    // (unreachable by walking, but click-to-sit's 170px range covers it).
    seats: [{ id: 'N', x: 1150, y: 325 }, { id: 'W', x: 1040, y: 400 }, { id: 'E', x: 1222, y: 418 }, { id: 'S', x: 1108, y: 432 }] },
];

// Per-table, per-game board placement overrides. When a table+game combination has an
// entry here it wins over the table's default contain-fit into top.{x,y,w,h}; `angle`
// is degrees (Phaser setAngle). Authored with the DEV Game Hall edit mode (press G in
// the hall) — different board renders sit differently on each felt, so tune per pair.
// Shape: { t1: { checkers: { x, y, w, h, angle } }, ... }
// Hand-tuned in the DEV edit mode 2026-07-09 (downstairs; games without an entry use the
// table's default `board` box).
export const BOARD_OVERRIDES = {
  t1: {
    go:         { x: 437, y: 550, w: 126, h: 84, angle: 0 },
    mancala:    { x: 443, y: 551, w: 134, h: 90, angle: 0 },
    makao:      { x: 438, y: 547, w: 142, h: 98, angle: 0 },
    backgammon: { x: 443, y: 549, w: 96,  h: 72, angle: 355 },
  },
  t2: { makao: { x: 771, y: 433, w: 62, h: 42, angle: 0 } },
  t3: { makao: { x: 1031, y: 407, w: 42, h: 30, angle: 0 } },
  t4: {
    go:      { x: 226, y: 404, w: 71, h: 48, angle: 0 },
    mancala: { x: 229, y: 403, w: 71, h: 46, angle: 0 },
    makao:   { x: 225, y: 402, w: 87, h: 58, angle: 0 },
  },
  t5: {
    chess:      { x: 593, y: 473, w: 97,  h: 79, angle: 0 },
    go:         { x: 585, y: 481, w: 127, h: 91, angle: 0 },
    mancala:    { x: 591, y: 479, w: 121, h: 83, angle: 0 },
    makao:      { x: 591, y: 475, w: 135, h: 99, angle: 0 },
    backgammon: { x: 591, y: 479, w: 107, h: 69, angle: 0 },
  },
  t6: { chess: { x: 780, y: 322, w: 41, h: 34, angle: 0 } },
};

export function rectContains(r, x, y) {
  return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
}

export function feetInAnyRect(rects, x, y) {
  for (const r of rects) if (rectContains(r, x, y)) return true;
  return false;
}
